/**
 * PATH: packages/ui/src/kanban/card-surface/fixtures/goldenCards.ts
 * FILE_ID: UI-KANBAN-CARDSURFACE-FIXTURES-001
 * PURPOSE: Provide stable lean/full Card payloads for every surface contract test.
 * WHY: Views must share one representative fixture set rather than inventing local Cards.
 * FLOW: fixture id → immutable wire payload + expected surface traits.
 * DEPENDENCIES: ../model/normalizeCardWire.ts.
 * RELATED: contract.test.ts; registry.ts; ../components/KanbanCard*.test.tsx.
 */
export interface GoldenCardFixture {
  wire: Readonly<Record<string, unknown>>
  expected: Readonly<{
    surface: 'tile' | 'reference'
    accessLocked: boolean
    supportsHoursInput?: boolean
  }>
}

export const GOLDEN_CARD_FIXTURES = {
  sparseLean: {
    wire: {
      id: 'card-sparse',
      organisation: 1,
      lane: 'lane-inbox',
      title: 'Review supplier invoice',
      status: 'OPEN',
      priority: 1,
      attachment_count: 2,
      comments_count: 0,
      checklist_summary: { total: 0, done: 0 },
      updated_at: '2026-07-15T00:00:00Z',
    },
    expected: { surface: 'tile', accessLocked: false },
  },
  rich: {
    wire: {
      id: 'card-rich',
      organisation: 1,
      lane: 'lane-doing',
      title: 'Coordinate structural steel delivery',
      summary: 'Confirm delivery window with the supplier and site manager.',
      status: 'BLOCKED',
      blocked_reason: 'Waiting for revised delivery schedule',
      priority: 3,
      primary_email: 'email-rich',
      related_emails: ['email-reply-1', 'email-reply-2'],
      related_attachment_ids_json: ['attachment-1'],
      attachment_count: 1,
      related_email_count: 2,
      comments_count: 3,
      checklist_summary: { total: 4, done: 2 },
      action_date: '2026-07-16',
      due_date: '2026-07-18',
      start_date: '2026-07-15',
      pinned: true,
      tags: [],
      assigned_user_ids: [7, 8],
      audit_log_json: [],
      lane_placements: [
        {
          id: 'placement-1',
          card: 'card-rich',
          lane: 'lane-finance',
          position: 20,
          lane_name: 'Finance review',
          board: 'board-finance',
          board_name: 'Finance',
        },
      ],
      updated_at: '2026-07-15T00:00:00Z',
    },
    expected: { surface: 'tile', accessLocked: false },
  },
  commentsOnly: {
    wire: {
      id: 'card-comments',
      organisation: 1,
      lane: 'lane-inbox',
      title: 'Clarify client variation',
      status: 'OPEN',
      comments_count: 2,
      checklist_summary: { total: 0, done: 0 },
    },
    expected: { surface: 'tile', accessLocked: false },
  },
  aiThread: {
    wire: {
      id: 'card-ai-thread',
      organisation: 1,
      lane: 'lane-inbox',
      title: 'Respond to approved variation request',
      status: 'OPEN',
      primary_email: 'email-ai-thread',
      related_email_count: 1,
      comments_count: 0,
      checklist_summary: { total: 0, done: 0 },
    },
    expected: { surface: 'tile', accessLocked: false },
  },
  workbook: {
    wire: {
      id: 'card-workbook',
      organisation: 1,
      lane: 'lane-planning',
      title: 'Build procurement comparison workbook',
      status: 'OPEN',
      document_kinds: ['workbook'],
      comments_count: 0,
      checklist_summary: { total: 0, done: 0 },
    },
    expected: { surface: 'tile', accessLocked: false },
  },
  accessLocked: {
    wire: {
      id: 'card-locked',
      organisation: 1,
      lane: 'lane-sensitive',
      title: 'Locked item',
      summary: '',
      status: 'OPEN',
      access_locked: true,
      access_lock_state: 'LOCKED',
      access_lock_id: 42,
      comments_count: 0,
      checklist_summary: { total: 0, done: 0 },
    },
    expected: { surface: 'tile', accessLocked: true },
  },
  quote: {
    wire: {
      id: 'card-quote',
      organisation: 1,
      lane: 'lane-quote',
      title: 'Quote 1042 — factory expansion',
      status: 'OPEN',
      external_provider: 'crm-quote',
      amount_cents: 4200000,
      win_pct: 65,
      quote_meta_json: { client: 'Acme Pty Ltd', quote_number: '1042' },
      comments_count: 0,
      checklist_summary: { total: 0, done: 0 },
    },
    expected: { surface: 'tile', accessLocked: false },
  },
  arAp: {
    wire: {
      id: 'card-ar-ap',
      organisation: 1,
      lane: 'lane-payables',
      title: 'Approve concrete supplier payment',
      status: 'OPEN',
      amount_cents: 125000,
      cash_direction: 'out',
      due_date: '2026-07-17',
      comments_count: 0,
      checklist_summary: { total: 0, done: 0 },
    },
    expected: { surface: 'tile', accessLocked: false },
  },
  timeDayHours: {
    wire: {
      id: 'card-time-day',
      organisation: 1,
      lane: 'lane-time',
      title: 'Install roof trusses',
      status: 'OPEN',
      due_date: '2026-07-15',
      comments_count: 0,
      checklist_summary: { total: 0, done: 0 },
    },
    expected: { surface: 'tile', accessLocked: false, supportsHoursInput: true },
  },
  legacyPlacementString: {
    wire: {
      id: 'card-legacy-placement',
      organisation: 1,
      lane: 'lane-inbox',
      title: 'Legacy placement payload',
      status: 'OPEN',
      lane_placements:
        '[{"id":"placement-json","card":"card-legacy-placement","lane":"lane-ops","position":1}]',
      comments_count: 0,
      checklist_summary: { total: 0, done: 0 },
    },
    expected: { surface: 'tile', accessLocked: false },
  },
} as const satisfies Record<string, GoldenCardFixture>

export type GoldenCardFixtureId = keyof typeof GOLDEN_CARD_FIXTURES
